import { cn } from "../lib/utils";
import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";
import { useLogout } from "../hooks/logoutAndRedirect";

const SidebarContext = createContext(undefined);


export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true
}) => {
  const [openState, setOpenState] = useState(true);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate: animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props) => {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...(props)} />
    </>
  );
};



export const DesktopSidebar = ({
  className,
  children,
  ...props
}) => {
  const { open, setOpen, animate } = useSidebar();
  const logoutAndRedirect = useLogout();


  return (
    <>
      <motion.div
        className={cn(
          "h-full px-2 py-4 hidden md:flex flex-col bg-black w-[280px] shrink-0",
          className
        )}
        animate={{
          width: animate ? (open ? "280px" : "70px") : "280px",
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        {...props}
      >
        <div className="flex flex-col h-full justify-between">
          {/* Top: Logo and Menu */}
          <div className="flex items-center justify-between px-2 py-4">
            {/* Logo Area */}
            <div className={cn(
              "flex items-center transition-all duration-300",
              open ? "justify-start w-auto" : "justify-center w-full"
            )}>
              <div className="text-3xl text-gray-700 dark:text-gray-200">
                
              </div>
              {open && (
                <span className="ml-2 font-bold text-gray-800 dark:text-white text-lg">
                  Motor Vehicles Law
                </span>
              )}
            </div>
          </div>

          {/* Separator */}
          <hr className="border-t border-gray-300 dark:border-gray-700 mb-2" />

          {/* Menu Area */}
          <div className="flex-1 space-y-1">
            <nav className="flex flex-col space-y-2 pt-9">
              {children}
            </nav>
          </div>

          {/* Separator */}
          <hr className="border-t border-gray-300 dark:border-gray-700 mb-2" />

          {/* Bottom: Profile/Footer */}
          <div className={cn(
            "flex items-center w-full transition-all px-2 py-4",
            open ? "justify-start gap-3" : "justify-center"
          )}>
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex-shrink-0" />

            {/* Profile Text */}
            {open && (
              <div className="flex flex-col">
                <button className="text-gray-800 dark:text-white font-medium text-sm text-left">
                  Admin User
                </button>
                <button className="text-sm text-red-500 py-2 hover:underline text-left"
                onClick={logoutAndRedirect}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};


export const MobileSidebar = ({
  className,
  children,
  ...props
}) => {
  const { open, setOpen } = useSidebar();
  return (
    <>
      <div
        className={cn(
          "h-10 px-4 py-4 flex flex-row md:hidden  items-center justify-between bg-neutral-100 dark:bg-neutral-800 w-full"
        )}
        {...props}>
        <div className="flex justify-end z-20 w-full">
          <IconMenu2
            className="text-neutral-800 dark:text-neutral-200"
            onClick={() => setOpen(!open)} />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={cn(
                "fixed h-full w-full inset-0 bg-white dark:bg-neutral-900 p-10 z-[100] flex flex-col justify-between",
                className
              )}>
              <div
                className="absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200"
                onClick={() => setOpen(!open)}>
                <IconX />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export const SidebarLink = ({ link, isSelected, onClick }) => {
  const { open } = useSidebar();

  return (
    <button
      onClick={onClick}
      className={cn(
        "group flex items-center transition-all duration-300 overflow-hidden",
        open ? "w-full justify-start px-3 py-2 rounded-md" : "w-14 h-14 justify-center",
        !open && isSelected ? "bg-transparent" : "", // No background for button when collapsed and selected
        open && isSelected ? "bg-yellow-500 text-white" : "hover:bg-gray-700 hover:text-white text-gray-400"
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex items-center justify-center transition-all",
          open ? "" : "w-10 h-10 rounded-full", // Circle only when collapsed
          isSelected
            ? "bg-yellow-500 text-white"
            : "text-gray-400 group-hover:text-white"
        )}
      >
        {React.cloneElement(link.icon, { size: 24 })}
      </div>

      {/* Label */}
      {open && (
        <span
          className={cn(
            "ml-3 text-sm font-medium whitespace-nowrap",
            isSelected ? "text-white" : "text-gray-700 dark:text-gray-300"
          )}
        >
          {link.label}
        </span>
      )}
    </button>
  );
};



